/**
 * Theme selector -- entry point for the capsule creator.
 *
 * Layout:
 *   - Fixed header (back + title)
 *   - Sticky category pill tabs (horizontal scroll)
 *   - Scrollable content:
 *     - "All" tab  -> vertical list of category sections, each with a
 *                     horizontal row of 9:16 phone-preview cards
 *     - Other tabs -> single horizontal row of that category's cards
 *
 * Adding a new theme:
 *   - Native theme -> add to THEME_REGISTRY in themes/registry.ts (category required)
 *   - HTML theme   -> add to BUILTIN_HTML_THEMES in lib/htmlThemes.ts (category required)
 *   -> It appears automatically in the correct section. No changes needed here.
 */

import {useCallback, useMemo, useState} from 'react'
import {
  FlatList,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native'
import {useSafeAreaInsets} from 'react-native-safe-area-context'
import {Feather} from '@expo/vector-icons'
import {useNavigation} from '@react-navigation/native'
import {type NativeStackScreenProps} from '@react-navigation/native-stack'

import {BUILTIN_HTML_THEMES} from '#/lib/htmlThemes'
import {type CommonNavigatorParams} from '#/lib/routes/types'
import {THEME_REGISTRY} from '#/themes/registry'
import {ThemeThumbnail} from '#/themes/thumbnails/ThemeThumbnail'
import {OVER_ART} from '#/themes-studio/overArt'
import {useStudioChrome} from '#/themes-studio/useChrome'
import {
  THEME_CATEGORIES,
  type ThemeCategory,
  type ThemePlugin,
} from '#/types/capsule'

// -- Unified item type for the selector ----------------------------------------

type NativeItem = {
  kind: 'native'
  id: string
  name: string
  description: string
  category: ThemeCategory
  plugin: ThemePlugin
}

type HtmlItem = {
  kind: 'html'
  id: string
  name: string
  description: string
  category: ThemeCategory
  htmlPath: string
  emoji: string
}

type ThemeItem = NativeItem | HtmlItem

// -- Card dimensions (9:16) ----------------------------------------------------

const CARD_W = 112
const CARD_H = Math.round((CARD_W * 16) / 9) // 199
const CARD_GAP = 12
const LIST_PADDING = 16

// -- Build item lists ----------------------------------------------------------

function buildAllItems(): ThemeItem[] {
  const natives: NativeItem[] = Object.values(THEME_REGISTRY).map(p => ({
    kind: 'native',
    id: p.id,
    name: p.name,
    description: p.description,
    category: p.category,
    plugin: p,
  }))

  const htmls: HtmlItem[] = BUILTIN_HTML_THEMES.map(h => ({
    kind: 'html',
    id: h.id,
    name: h.name,
    description: h.emoji + ' ' + h.name,
    category: h.category,
    htmlPath: h.path,
    emoji: h.emoji,
  }))

  return [...natives, ...htmls]
}

// -- Category section -- horizontal card row -----------------------------------

function CategoryRow({
  items,
  onSelect,
}: {
  items: ThemeItem[]
  onSelect: (item: ThemeItem) => void
}) {
  return (
    <FlatList
      horizontal
      data={items}
      keyExtractor={item => item.id}
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{paddingHorizontal: LIST_PADDING, gap: CARD_GAP}}
      renderItem={({item}) => (
        <ThemeCard item={item} onPress={() => onSelect(item)} />
      )}
    />
  )
}

// -- Single 9:16 theme card ----------------------------------------------------

function ThemeCard({item, onPress}: {item: ThemeItem; onPress: () => void}) {
  const c = useStudioChrome()
  return (
    <TouchableOpacity
      accessibilityRole="button"
      onPress={onPress}
      activeOpacity={0.82}
      style={s.card}>
      {/* 9:16 phone-screen thumbnail */}
      <View style={[s.thumbWrap, {width: CARD_W, height: CARD_H}]}>
        <ThemeThumbnail themeId={item.id} width={CARD_W} />
        {/* HTML badge */}
        {item.kind === 'html' && (
          <View style={s.htmlBadge}>
            <Text style={[s.htmlBadgeTxt, {color: c.onCanvas}]}>HTML</Text>
          </View>
        )}
      </View>
      {/* Name below thumbnail */}
      <Text style={[s.cardName, {color: c.text}]} numberOfLines={2}>
        {item.kind === 'html' ? item.emoji + ' ' + item.name : item.name}
      </Text>
    </TouchableOpacity>
  )
}

// -- Embeddable view (no navigation dependency) --------------------------------

export function CapsuleThemeSelectorView({
  onSelect,
  onBack,
  showSafeAreaTop = true,
  hideHeader = false,
}: {
  onSelect: (themeId: string, htmlThemeUrl?: string) => void
  onBack: () => void
  showSafeAreaTop?: boolean
  hideHeader?: boolean
}) {
  const insets = useSafeAreaInsets()
  const c = useStudioChrome()

  const [activeCategory, setActiveCategory] = useState<'all' | ThemeCategory>(
    'all',
  )
  const allItems = useMemo(() => buildAllItems(), [])

  const byCategory = useMemo(() => {
    const map = new Map<ThemeCategory, ThemeItem[]>()
    for (const item of allItems) {
      const list = map.get(item.category) ?? []
      list.push(item)
      map.set(item.category, list)
    }
    return map
  }, [allItems])

  const populatedCategories = useMemo(
    () => THEME_CATEGORIES.filter(c => byCategory.has(c.id)),
    [byCategory],
  )

  const tabCategories = useMemo(() => THEME_CATEGORIES, [])

  const handleSelect = useCallback(
    (item: ThemeItem) => {
      if (item.kind === 'native') {
        onSelect(item.id)
      } else {
        onSelect('html-bridge', item.htmlPath)
      }
    },
    [onSelect],
  )

  const topPadding = (showSafeAreaTop ? insets.top : 0) + 12

  // -- Render ------------------------------------------------------------------

  const pillBg = (id: 'all' | ThemeCategory) =>
    activeCategory === id ? c.accentFill : c.surface
  const pillTxt = (id: 'all' | ThemeCategory) =>
    activeCategory === id ? c.onAccent : c.textMuted
  const pillBorder = (id: 'all' | ThemeCategory) =>
    activeCategory === id ? 'transparent' : c.borderStrong

  return (
    <View style={[s.root, {backgroundColor: c.frame}]}>
      {/* -- Header (hidden when embedded in a parent with its own header) -- */}
      {!hideHeader && (
        <View style={[s.header, {paddingTop: topPadding}]}>
          <Pressable
            accessibilityRole="button"
            onPress={onBack}
            style={[s.backBtn, {borderColor: c.borderStrong}]}>
            <Feather name="arrow-left" size={19} color={c.text} />
          </Pressable>
          <View style={{flex: 1}}>
            <Text style={[s.title, {color: c.text}]}>Choose a Theme</Text>
            <Text style={[s.subtitle, {color: c.textMuted}]}>
              Pick the look & feel of your capsule
            </Text>
          </View>
        </View>
      )}

      {/* -- Category pill tabs -- */}
      <View style={s.pillsWrapper}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={s.pillsScroll}>
          {/* "All" pill */}
          <TouchableOpacity
            accessibilityRole="button"
            onPress={() => setActiveCategory('all')}
            style={[
              s.pill,
              {
                backgroundColor: pillBg('all'),
                borderColor: pillBorder('all'),
              },
            ]}>
            <Text style={[s.pillTxt, {color: pillTxt('all')}]}>All</Text>
          </TouchableOpacity>

          {/* Category pills */}
          {tabCategories.map(cat => {
            const hasItems = byCategory.has(cat.id)
            return (
              <TouchableOpacity
                accessibilityRole="button"
                key={cat.id}
                onPress={() => setActiveCategory(cat.id)}
                style={[
                  s.pill,
                  {
                    backgroundColor: pillBg(cat.id),
                    borderColor: pillBorder(cat.id),
                    opacity: hasItems ? 1 : 0.45,
                  },
                ]}>
                <Text style={s.pillEmoji}>{cat.emoji}</Text>
                <Text style={[s.pillTxt, {color: pillTxt(cat.id)}]}>
                  {cat.label}
                </Text>
                {!hasItems && (
                  <View style={[s.soonPill, {backgroundColor: c.fillStrong}]}>
                    <Text style={[s.soonPillTxt, {color: c.textMuted}]}>
                      Soon
                    </Text>
                  </View>
                )}
              </TouchableOpacity>
            )
          })}
        </ScrollView>
      </View>

      {/* -- Content -- */}
      <ScrollView
        style={{flex: 1}}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          paddingBottom: insets.bottom + 32,
          paddingTop: 8,
        }}>
        {activeCategory === 'all'
          ? /* All view -- section per populated category */
            populatedCategories.map(cat => {
              const items = byCategory.get(cat.id) ?? []
              return (
                <View key={cat.id} style={s.section}>
                  {/* Section header */}
                  <View style={s.sectionHeader}>
                    <Text style={s.sectionEmoji}>{cat.emoji}</Text>
                    <View>
                      <Text style={[s.sectionLabel, {color: c.text}]}>
                        {cat.label}
                      </Text>
                      <Text style={[s.sectionDesc, {color: c.textMuted}]}>
                        {cat.description}
                      </Text>
                    </View>
                    <Text style={[s.sectionCount, {color: c.textMuted}]}>
                      {items.length}
                    </Text>
                  </View>
                  {/* Horizontal card row */}
                  <CategoryRow items={items} onSelect={handleSelect} />
                </View>
              )
            })
          : /* Filtered view -- single category */
            (() => {
              const items = byCategory.get(activeCategory) ?? []
              const catMeta = THEME_CATEGORIES.find(
                c => c.id === activeCategory,
              )!
              if (items.length === 0) {
                return (
                  <View style={s.emptySection}>
                    <Text style={{fontSize: 40, marginBottom: 12}}>
                      {catMeta.emoji}
                    </Text>
                    <Text style={[s.emptyTitle, {color: c.text}]}>
                      {catMeta.label} themes coming soon
                    </Text>
                    <Text style={[s.emptyDesc, {color: c.textMuted}]}>
                      {catMeta.description}
                    </Text>
                  </View>
                )
              }
              return (
                <View style={s.section}>
                  <View style={s.sectionHeader}>
                    <Text style={s.sectionEmoji}>{catMeta.emoji}</Text>
                    <View>
                      <Text style={[s.sectionLabel, {color: c.text}]}>
                        {catMeta.label}
                      </Text>
                      <Text style={[s.sectionDesc, {color: c.textMuted}]}>
                        {catMeta.description}
                      </Text>
                    </View>
                    <Text style={[s.sectionCount, {color: c.textMuted}]}>
                      {items.length}
                    </Text>
                  </View>
                  <CategoryRow items={items} onSelect={handleSelect} />
                </View>
              )
            })()}

        {/* Footer hint */}
        <View style={s.footer}>
          <Feather name="info" size={12} color={c.textMuted} />
          <Text style={[s.footerTxt, {color: c.textMuted}]}>
            You can change the theme at any time in the editor
          </Text>
        </View>
      </ScrollView>
    </View>
  )
}

// -- Navigation screen wrapper -------------------------------------------------

type Props = NativeStackScreenProps<
  CommonNavigatorParams,
  'CapsuleThemeSelector'
>

export function CapsuleThemeSelectorScreen({route: _route}: Props) {
  const navigation = useNavigation<Props['navigation']>()
  return (
    <CapsuleThemeSelectorView
      onBack={() => navigation.goBack()}
      onSelect={(themeId, htmlThemeUrl) => {
        if (htmlThemeUrl) {
          navigation.navigate('CapsuleStudio', {themeId, htmlThemeUrl})
        } else {
          navigation.navigate('CapsuleStudio', {themeId})
        }
      }}
    />
  )
}

// -- Styles --------------------------------------------------------------------

const s = StyleSheet.create({
  root: {flex: 1},

  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingBottom: 14,
  },
  backBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {fontSize: 20, fontWeight: '800', lineHeight: 24},
  subtitle: {fontSize: 12, marginTop: 1},

  // Category pills
  pillsWrapper: {paddingBottom: 12},
  pillsScroll: {paddingHorizontal: 16, gap: 8},
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 20,
    borderWidth: 1,
  },
  pillEmoji: {fontSize: 13},
  pillTxt: {fontSize: 13, fontWeight: '600'},
  soonPill: {
    paddingHorizontal: 5,
    paddingVertical: 1,
    borderRadius: 4,
  },
  soonPillTxt: {fontSize: 9, fontWeight: '700'},

  // Sections
  section: {marginBottom: 28},
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: LIST_PADDING,
    marginBottom: 12,
  },
  sectionEmoji: {fontSize: 22},
  sectionLabel: {fontSize: 16, fontWeight: '700'},
  sectionDesc: {fontSize: 12, marginTop: 1},
  sectionCount: {marginLeft: 'auto', fontSize: 12, fontWeight: '600'},

  // Cards
  card: {alignItems: 'center', gap: 8, width: CARD_W},
  thumbWrap: {borderRadius: 12, overflow: 'hidden'},
  cardName: {
    fontSize: 11,
    fontWeight: '700',
    textAlign: 'center',
    lineHeight: 15,
    paddingHorizontal: 2,
  },

  /*
   * The HTML badge floats *over* the theme's own thumbnail, so it is the one
   * thing here that cannot wear a chrome surface: the picture behind it is the
   * theme's art, which is neither light nor dark by the app's appearance. Its
   * fill is therefore an opaque marker — the same rule the canvas floats over
   * the editor follow — and only its ink is a role, the chrome's ink-over-content.
   *
   * The marker's own fill is declared with the rest of the over-art furniture
   * (`themes-studio/overArt.ts`) rather than inlined here, so this screen names
   * no colour at all.
   */
  htmlBadge: {
    position: 'absolute',
    top: 6,
    right: 6,
    backgroundColor: OVER_ART.badgeOnArt,
    paddingHorizontal: 5,
    paddingVertical: 2,
    borderRadius: 4,
  },
  htmlBadgeTxt: {
    fontSize: 9,
    fontWeight: '800',
    letterSpacing: 0.5,
  },

  // Soon label inside card
  soonTxt: {fontSize: 11, fontWeight: '600'},

  // Empty state
  emptySection: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
    paddingHorizontal: 32,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 8,
  },
  emptyDesc: {fontSize: 14, textAlign: 'center', lineHeight: 20},

  // Footer
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    marginTop: 8,
    paddingHorizontal: 16,
  },
  footerTxt: {fontSize: 12},
})
